import { useCallback, useEffect, useMemo, useState } from "react";
import { useSDK } from "../lib/sdk-context";
import { api } from "../lib/api";
import { Button } from "../components/ui/Button";
import { useToast } from "../components/ui/Toast";
import { checkAdminSession } from "../lib/admin-auth";
import ChainCandidateCard, {
  type ChainCandidate,
  type MergeTarget,
} from "../components/ChainCandidateCard";

type Decision =
  | "pending"
  | "approved"
  | "rejected"
  | "merged"
  | "auto_approve_eligible"
  | "auto_merge_eligible";

interface BucketCounts {
  pending: number;
  approved: number;
  rejected: number;
  merged: number;
  auto_approve_eligible: number;
  auto_merge_eligible: number;
  total: number;
}

interface ApprovedChain {
  chain_id: string;
  display_name: string;
  canonical_root: string;
  status: string;
}

const DECISION_TONES: Record<Decision, "auto" | "user" | "review" | "unmatched"> = {
  pending: "review",
  approved: "auto",
  rejected: "unmatched",
  merged: "user",
  auto_approve_eligible: "auto",
  auto_merge_eligible: "user",
};

const DECISION_LABEL: Record<Decision, string> = {
  pending: "PENDING",
  approved: "APPROVED",
  rejected: "REJECTED",
  merged: "MERGED",
  auto_approve_eligible: "AUTO-APPROVE ELIGIBLE",
  auto_merge_eligible: "AUTO-MERGE ELIGIBLE",
};

export default function Chains() {
  const { context } = useSDK();
  const toast = useToast();

  // PR #66: defense-in-depth for the canonical chain directory (admin-only).
  // App.tsx's synchronous route guard handles the common paths, but if this page
  // is reached directly: a brand session bounces to its own chain list, and any
  // viewer who is neither admin nor brand is sent home.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch("/api/auth/me", { credentials: "include" });
        const j = r.ok ? await r.json() : null;
        if (!alive) return;
        if (j && j.user?.role !== "admin" && j.tenant?.slug) {
          window.location.replace(`#/brand/${j.tenant.slug}/my-chains`);
          return;
        }
        const isAdmin = j?.user?.role === "admin" || (await checkAdminSession());
        if (!alive) return;
        if (!isAdmin) window.location.replace("#/");
      } catch {
        /* network error — leave the page as-is */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const [activeBucket, setActiveBucket] = useState<Decision>("pending");
  const [counts, setCounts] = useState<BucketCounts>({ pending: 0, approved: 0, rejected: 0, merged: 0, auto_approve_eligible: 0, auto_merge_eligible: 0, total: 0 });
  const [applying, setApplying] = useState(false);
  const [rows, setRows] = useState<ChainCandidate[]>([]);
  const [chains, setChains] = useState<ApprovedChain[]>([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [skipped, setSkipped] = useState<Set<string>>(new Set());

  const loadCounts = useCallback(async () => {
    try {
      const r = await api.get(context, "/api/admin/chains/candidates/buckets");
      setCounts({ pending: 0, approved: 0, rejected: 0, merged: 0, auto_approve_eligible: 0, auto_merge_eligible: 0, total: 0, ...r.counts });
    } catch (err) {
      toast.push(`Failed to load counts: ${(err as Error).message}`, "error");
    }
  }, [context, toast]);

  const loadRows = useCallback(async (decision: Decision) => {
    setLoading(true);
    try {
      const r = await api.get(context, `/api/admin/chains/candidates?decision=${decision}&limit=200`);
      setRows(r.rows || []);
    } catch (err) {
      toast.push(`Failed to load: ${(err as Error).message}`, "error");
    } finally {
      setLoading(false);
    }
  }, [context, toast]);

  const loadChains = useCallback(async () => {
    try {
      const r = await api.get(context, "/api/admin/chains/list");
      setChains(r.rows || []);
    } catch {
      // non-fatal
    }
  }, [context]);

  useEffect(() => { loadCounts(); loadChains(); }, [loadCounts, loadChains]);
  useEffect(() => { loadRows(activeBucket); }, [activeBucket, loadRows]);

  const filtered = useMemo(
    () => rows.filter((r) => !skipped.has(r.candidate_id)),
    [rows, skipped]
  );

  const reviewedCount = counts.approved + counts.rejected + counts.merged;
  const pctReviewed = counts.total > 0 ? Math.round((reviewedCount / counts.total) * 100) : 0;

  const mergeTargets = useMemo<MergeTarget[]>(() => {
    const candTargets: MergeTarget[] = rows.map((c) => ({
      kind: "candidate", id: c.candidate_id,
      label: c.candidate_display_name,
      sublabel: `${c.member_license_numbers.length} members`,
    }));
    const chainTargets: MergeTarget[] = chains.map((c) => ({
      kind: "chain", id: c.chain_id, label: c.display_name, sublabel: c.canonical_root,
    }));
    return [...chainTargets, ...candTargets];
  }, [rows, chains]);

  const runDetection = async () => {
    setRunning(true);
    try {
      const r = await api.post(context, "/api/admin/chains/detect", {});
      toast.push(
        `Detected ${(r.inserted ?? 0) + (r.updated ?? 0)} candidates: ${r.inserted ?? 0} new, ${r.updated ?? 0} updated, ${r.retired ?? 0} retired, ${r.skipped_already_approved ?? 0} already approved`,
        "success"
      );
      await loadCounts();
      await loadRows(activeBucket);
    } catch (err) {
      toast.push(`Detection failed: ${(err as Error).message}`, "error");
    } finally {
      setRunning(false);
    }
  };

  const applyAutoEligible = async () => {
    setApplying(true);
    try {
      const r = await api.post(context, "/api/admin/detector/apply-auto-eligible", {});
      toast.push(
        `Applied auto-eligible: ${r.approved ?? 0} approved, ${r.merged ?? 0} merged, ${r.skipped ?? 0} skipped`,
        "success"
      );
      await loadCounts();
      await loadRows(activeBucket);
      await loadChains();
    } catch (err) {
      toast.push(`Apply failed: ${(err as Error).message}`, "error");
    } finally {
      setApplying(false);
    }
  };

  const autoEligibleTotal = counts.auto_approve_eligible + counts.auto_merge_eligible;

  const onApprove: React.ComponentProps<typeof ChainCandidateCard>["onApprove"] = async (c, payload) => {
    try {
      const r = await api.post(context, `/api/admin/chains/candidates/${c.candidate_id}/approve`, payload);
      toast.push(`Approved "${r.display_name}" with ${r.member_count} member${r.member_count === 1 ? "" : "s"}`, "success");
      await loadCounts();
      await loadRows(activeBucket);
      await loadChains();
    } catch (err) {
      toast.push(`Approve failed: ${(err as Error).message}`, "error");
    }
  };

  const onReject: React.ComponentProps<typeof ChainCandidateCard>["onReject"] = async (c, reason) => {
    try {
      await api.post(context, `/api/admin/chains/candidates/${c.candidate_id}/reject`, { reason });
      toast.push("Rejected.", "success");
      await loadCounts();
      await loadRows(activeBucket);
    } catch (err) {
      toast.push(`Reject failed: ${(err as Error).message}`, "error");
    }
  };

  const onMerge: React.ComponentProps<typeof ChainCandidateCard>["onMerge"] = async (c, payload) => {
    try {
      await api.post(context, `/api/admin/chains/candidates/${c.candidate_id}/merge`, payload);
      toast.push("Merged.", "success");
      await loadCounts();
      await loadRows(activeBucket);
    } catch (err) {
      toast.push(`Merge failed: ${(err as Error).message}`, "error");
    }
  };

  const onSplit: React.ComponentProps<typeof ChainCandidateCard>["onSplit"] = async (c, removeList) => {
    try {
      const r = await api.post(context, `/api/admin/chains/candidates/${c.candidate_id}/split`, { license_numbers_to_remove: removeList });
      toast.push(`Split — ${r.remaining_count} member${r.remaining_count === 1 ? "" : "s"} remain.`, "success");
      await loadRows(activeBucket);
    } catch (err) {
      toast.push(`Split failed: ${(err as Error).message}`, "error");
    }
  };

  const onSkip = (c: ChainCandidate) => {
    setSkipped((prev) => new Set(prev).add(c.candidate_id));
  };

  const onLocationAdded = async (_c: ChainCandidate) => {
    toast.push("Location added.", "success");
    await loadRows(activeBucket);
  };

  const pill = (key: Decision, label: string) => {
    const active = activeBucket === key;
    const tone = DECISION_TONES[key];
    const toneClasses = {
      auto: active ? "bg-[#F1F8F2] text-[#1C5125] border-[#BFE0C4]" : "bg-white text-[#1C5125] border-[#BFE0C4]/60 hover:bg-[#F1F8F2]/60",
      user: active ? "bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]" : "bg-white text-[#065F46] border-[#A7F3D0]/60 hover:bg-[#ECFDF5]/60",
      review: active ? "bg-[#FFF7ED] text-[#9A3412] border-[#FED7AA]" : "bg-white text-[#9A3412] border-[#FED7AA]/60 hover:bg-[#FFF7ED]/60",
      unmatched: active ? "bg-[#FEF2F2] text-[#991B1B] border-[#FECACA]" : "bg-white text-[#991B1B] border-[#FECACA]/60 hover:bg-[#FEF2F2]/60",
    }[tone];
    return (
      <button
        key={key}
        onClick={() => setActiveBucket(key)}
        className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${toneClasses} ${active ? "ring-1 ring-inset ring-current/20" : ""}`}
      >
        {label} <span className="font-semibold">{(counts[key] ?? 0).toLocaleString()}</span>
      </button>
    );
  };

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Chain approval queue</h2>
          <p className="text-sm text-gray-600 mt-1">
            {loading ? "Loading…" : `${counts.total.toLocaleString()} candidate chain${counts.total === 1 ? "" : "s"} detected`}
          </p>
          {!loading && counts.total > 0 && (
            <p className="text-xs text-gray-500 mt-0.5">
              Reviewed: <span className="font-medium text-gray-700">{reviewedCount.toLocaleString()}</span>
              {" · "}
              Pending: <span className="font-medium text-gray-700">{counts.pending.toLocaleString()}</span>
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="primary" onClick={runDetection} disabled={running}>
            {running ? "Running detection…" : "Run detection"}
          </Button>
          {autoEligibleTotal > 0 && (
            <Button variant="secondary" onClick={applyAutoEligible} disabled={applying}>
              {applying ? "Applying…" : `Apply all auto-eligible (${autoEligibleTotal})`}
            </Button>
          )}
          <Button variant="ghost" onClick={() => (window.location.hash = "#/review")}>Back to Review</Button>
        </div>
      </div>

      {!loading && counts.total > 0 && (
        <div className="mb-4">
          <div className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
            <div className="h-full bg-accent-500 transition-all" style={{ width: `${pctReviewed}%` }} />
          </div>
          <p className="mt-1 text-xs text-gray-500">{pctReviewed}% reviewed</p>
        </div>
      )}

      <div className="mb-6 flex flex-wrap gap-2">
        {pill("pending", DECISION_LABEL.pending)}
        {pill("auto_approve_eligible", DECISION_LABEL.auto_approve_eligible)}
        {pill("auto_merge_eligible", DECISION_LABEL.auto_merge_eligible)}
        {pill("approved", DECISION_LABEL.approved)}
        {pill("rejected", DECISION_LABEL.rejected)}
        {pill("merged", DECISION_LABEL.merged)}
      </div>

      {loading ? (
        <div className="rounded-lg border border-gray-200 bg-white p-10 text-center text-gray-500 text-sm">Loading chain queue…</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-10 text-center">
          <p className="text-gray-700 text-sm font-medium">Nothing here.</p>
          <p className="text-gray-500 text-xs mt-1">
            {counts.total === 0
              ? 'Click "Run detection" to scan licenses for chain candidates.'
              : "No candidates in this bucket."}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((c) => (
            <ChainCandidateCard
              key={c.candidate_id}
              candidate={c}
              mergeTargets={mergeTargets.filter((t) => !(t.kind === "candidate" && t.id === c.candidate_id))}
              onApprove={onApprove}
              onReject={onReject}
              onMerge={onMerge}
              onSplit={onSplit}
              onSkip={onSkip}
              onLocationAdded={onLocationAdded}
            />
          ))}
        </div>
      )}
    </div>
  );
}
